import { useTranslation, Trans } from 'react-i18next';
import { PauseCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import type { SuspendedAgency } from '@/auth/AgencyProvider';
import { supportEmail } from '@/lib/config';

/**
 * Every office membership of this account is in an agency the operator suspended (change spec
 * operator-agency-onboarding, design 2). AppLayout renders it full screen, with no navigation: only
 * the brand bar with the language switch and the sign-out. It says which agency, since when, that the data is kept, and whom to write to
 * (`supportEmail` of the runtime config). It never shows a reason: the portal is never told one (Q4).
 */
export function SuspendedAgencyScreen({ agencies }: { agencies: SuspendedAgency[] }) {
  const { t, i18n } = useTranslation();
  const email = supportEmail();
  const date = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
      ? ''
      : new Intl.DateTimeFormat(i18n.resolvedLanguage === 'es' ? 'es-US' : 'en-US', { dateStyle: 'medium' }).format(d);
  };
  const single = agencies.length === 1 ? agencies[0] : null;

  return (
    <Card className="mx-auto max-w-2xl">
      <CardContent className="flex flex-col items-center gap-4 p-12 text-center">
        <span className="grid size-12 place-items-center rounded-full bg-accent-wash">
          <PauseCircle className="size-6 text-accent-base" aria-hidden="true" />
        </span>
        <h1 className="text-2xl font-semibold text-ink">
          {single ? t('agency.suspended.title', { agency: single.name }) : t('agency.suspended.titleMany')}
        </h1>
        {single ? (
          <p className="text-base text-ink-secondary">{t('agency.suspended.body', { date: date(single.suspendedAt) })}</p>
        ) : (
          <>
            <ul className="flex flex-col gap-1 text-base text-ink-secondary">
              {agencies.map((a) => (
                <li key={a.id}>{t('agency.suspended.line', { agency: a.name, date: date(a.suspendedAt) })}</li>
              ))}
            </ul>
            <p className="text-base text-ink-secondary">{t('agency.suspended.bodyMany')}</p>
          </>
        )}
        {email ? (
          <p className="text-base text-ink">
            <Trans
              i18nKey="agency.suspended.contact"
              values={{ email }}
              components={{ mail: <a href={`mailto:${email}`} className="font-medium text-accent-base underline" /> }}
            />
          </p>
        ) : (
          <p className="text-base text-ink">{t('agency.suspended.contactNoEmail')}</p>
        )}
      </CardContent>
    </Card>
  );
}
